<script setup lang="ts">
import { BLOCK_DND_COOLDOWN_MS } from "../../composables/useBlockDnd";

const props = defineProps({
  targetId: {
    type: String,
    required: true,
  },
  index: {
    type: Number,
    default: null,
  },
  // Restrict which block types may be dropped here (by block `type`).
  filterType: {
    type: Array,
    default: () => [],
  },
  editable: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["move", "add-block"]);

const {
  draggedTemplate,
  draggedItemId,
  isDragActive,
  dropZonesVisible,
  activeDropZoneId,
  endBlockDrag,
  setActiveDropZone,
} = useBlockDnd();

// A new block coming from the palette is blocked when this drop zone declares a
// filter that doesn't include the dragged block's type. Existing-block moves are
// not type-filtered here (parity with previous behaviour).
const blockedByFilter = computed(() => {
  const filter = props.filterType as string[];
  if (!filter || filter.length === 0) return draggedTemplate.value?.hiddenUntilFiltered === true;
  const type = draggedTemplate.value?.type;
  if (!type) return false;
  return !filter.includes(type);
});

const isVisible = computed(
  () => props.editable && dropZonesVisible.value && !blockedByFilter.value,
);
const dropZoneId = computed(
  () => `${props.targetId}:${props.index ?? "end"}`,
);

// Use an enter/leave depth counter so hovering over inner content doesn't flicker.
const dragDepth = ref(0);
const leaveTimer = shallowRef<ReturnType<typeof setTimeout> | null>(null);
const isDraggingOver = computed(
  () => activeDropZoneId.value === dropZoneId.value,
);

const cancelLeaveTimer = () => {
  if (leaveTimer.value === null) return;
  clearTimeout(leaveTimer.value);
  leaveTimer.value = null;
};

const handleDragEnter = (event: DragEvent) => {
  if (!props.editable || !isDragActive.value) return;
  event.preventDefault();
  cancelLeaveTimer();
  dragDepth.value += 1;
  setActiveDropZone(dropZoneId.value);
};

const handleDragOver = (event: DragEvent) => {
  if (!props.editable || !isDragActive.value) return;
  // Must preventDefault on every dragover for `drop` to fire, and the
  // dropEffect has to match the source's effectAllowed or the drop is rejected.
  event.preventDefault();
  if (event.dataTransfer) {
    event.dataTransfer.dropEffect = draggedTemplate.value ? "copy" : "move";
  }
};

const handleDragLeave = () => {
  dragDepth.value -= 1;
  if (dragDepth.value <= 0) {
    dragDepth.value = 0;
    cancelLeaveTimer();
    leaveTimer.value = setTimeout(() => {
      if (activeDropZoneId.value === dropZoneId.value) {
        setActiveDropZone(null);
      }
      leaveTimer.value = null;
    }, BLOCK_DND_COOLDOWN_MS);
  }
};

const handleDrop = (event: DragEvent) => {
  event.preventDefault();
  cancelLeaveTimer();
  dragDepth.value = 0;
  setActiveDropZone(null);

  if (!props.editable || !isDragActive.value || blockedByFilter.value) return;

  if (draggedTemplate.value) {
    emit("add-block", {
      template: draggedTemplate.value.template,
      type: draggedTemplate.value.type,
      index: props.index,
    });
  } else if (draggedItemId.value) {
    emit("move", { targetId: props.targetId, index: props.index });
  }
  endBlockDrag();
};

onBeforeUnmount(cancelLeaveTimer);
</script>

<template>
  <div
    v-show="isVisible"
    class="relative z-20 mx-2 my-1 flex items-center justify-center rounded-md border-2 border-dashed transition-colors"
    :class="[
      isDragActive ? 'pointer-events-auto' : 'pointer-events-none',
      isDraggingOver
        ? 'border-primary bg-primary/15'
        : 'border-primary/30 bg-primary/5',
    ]"
    :data-dragging-over="isDraggingOver || undefined"
    @dragenter="handleDragEnter"
    @dragover="handleDragOver"
    @dragleave="handleDragLeave"
    @drop="handleDrop"
  >
    <span
      aria-hidden="true"
      class="absolute inset-x-0 -inset-y-2"
    />
    <div
      class="pointer-events-none flex items-center justify-center gap-1.5 py-1.5 text-[11px] font-medium uppercase tracking-wide"
      :class="isDraggingOver ? 'text-primary' : 'text-primary/70'"
    >
      <icon mode="svg" size="14" name="lucide:plus" />
      Drop here
    </div>
  </div>
</template>
